import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { FavoritosContext } from "../../context/Favoritos/FavoritosContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

// A etiqueta do ticket so vem completa na carga da lista (LOAD_TICKETS) e nos
// eventos que mexem em etiqueta (SyncTags/AddTag), que trazem ticket.tags.
// Os eventos comuns — mensagem nova (appMessage) e mudanca de status — mandam
// o ticket SEM o campo tags; ao trocar o ticket inteiro, a etiqueta sumia da
// lista ate o F5. Aqui, quando o payload nao traz tags, preservamos as que ja
// estavam na tela; quando traz (mesmo lista vazia, ao remover todas), a nova
// lista vence. Feito igual ao nome, que sobrevive por vir dentro de contact.
// O mesmo vale para o atendente (ticket.user): ele vem na carga da lista e nos
// eventos de update do ticket, mas alguns payloads chegam sem o campo. Quando
// nao vier, mantemos o que ja estava na tela, para o nome do vendedor nao
// piscar e sumir do card a cada mensagem nova.
const mesclarTicket = (antigo, novo) => {
	const mesclado = Array.isArray(novo.tags)
		? { ...novo }
		: { ...novo, tags: antigo?.tags };

	if (mesclado.user === undefined && mesclado.userId === antigo?.userId) {
		mesclado.user = antigo?.user;
	}

	return mesclado;
};

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = mesclarTicket(state[ticketIndex], ticket);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = mesclarTicket(state[ticketIndex], ticket);
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	// A etiqueta vive DUAS vezes na tela: na pagina de Etiquetas e, copiada,
	// dentro de cada ticket que a usa. O evento "tag" trazia so a etiqueta, e
	// nada reescrevia as copias — por isso trocar a cor so aparecia no F5, que
	// buscava os tickets de novo.
	if (action.type === "UPDATE_TAG_NOS_TICKETS") {
		const tag = action.payload;
		let mudou = false;

		state.forEach(ticket => {
			if (!Array.isArray(ticket.tags)) return;

			ticket.tags = ticket.tags.map(t => {
				if (t.id !== tag.id) return t;
				mudou = true;
				return { ...t, ...tag };
			});
		});

		return mudou ? [...state] : state;
	}

	if (action.type === "REMOVE_TAG_DOS_TICKETS") {
		const tagId = action.payload;
		let mudou = false;

		state.forEach(ticket => {
			if (!Array.isArray(ticket.tags)) return;

			const antes = ticket.tags.length;
			ticket.tags = ticket.tags.filter(t => t.id !== tagId);
			if (ticket.tags.length !== antes) mudou = true;
		});

		return mudou ? [...state] : state;
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

	const TicketsList = (props) => {
		const { status, searchParam, showAll, selectedQueueIds, filterTag, filterQueue, filterWhatsapp, filterUser, filterDate, dateFrom, dateTo, filterFavorite, mostrarOcultos, updateCount, style } =
			props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [recarga, setRecarga] = useState(0);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, searchParam, dispatch, showAll, selectedQueueIds, filterTag, filterQueue, filterWhatsapp, filterUser, filterDate, dateFrom, dateTo, filterFavorite, mostrarOcultos]);

	const { ehFixado } = useContext(FavoritosContext);

	const { tickets, hasMore, loading, count } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: JSON.stringify(selectedQueueIds),
		tags: filterTag,
		users: filterUser,
		whatsapps: filterWhatsapp,
		queues: filterQueue,
		date: filterDate,
		dateFrom,
		dateTo,
		filterFavorite,
		mostrarOcultos,
		recarga,
	});

	// Tempo real que caiu e voltou (wi-fi oscilou, computador suspendeu, aba
	// congelada pelo Chrome): o que chegou nesse meio-tempo nao veio pelo
	// socket. Busca a lista de novo, sem limpar a tela.
	const jaConectou = React.useRef(false);
	const ressincronizar = React.useCallback(() => {
		setPageNumber(1);
		setRecarga(r => r + 1);
	}, []);

	useEffect(() => {
		let escondidaDesde = 0;
		const aoMudarVisibilidade = () => {
			if (document.visibilityState === "hidden") {
				escondidaDesde = Date.now();
			} else if (escondidaDesde && Date.now() - escondidaDesde > 30000) {
				ressincronizar();
			}
		};
		document.addEventListener("visibilitychange", aoMudarVisibilidade);
		return () => document.removeEventListener("visibilitychange", aoMudarVisibilidade);
	}, [ressincronizar]);

	useEffect(() => {
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	useEffect(() => {
		const socket = openSocket();

		const shouldUpdateTicket = (ticket) => {
			if (!ticket || searchParam) return false;
			const hasQueueFilter = Array.isArray(selectedQueueIds) && selectedQueueIds.length > 0;
			const matchesQueue = !hasQueueFilter || !ticket.queueId || selectedQueueIds.includes(ticket.queueId);
			const matchesUser = showAll || !ticket.userId || ticket.userId === user?.id;
			const matchesStatus = !status || ticket.status === status;
			return matchesStatus && matchesUser && matchesQueue;
		};

		const onConnect = () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
			if (jaConectou.current) ressincronizar();
			jaConectou.current = true;
		};

		socket.on("connect", onConnect);
		if (socket.connected) {
			onConnect();
		}

		const aoAtualizarTicket = (data) => {
			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			if (data.action === "update" && data.ticket) {
				if (shouldUpdateTicket(data.ticket)) {
					dispatch({
						type: "UPDATE_TICKET",
						payload: data.ticket,
					});
				} else {
					dispatch({
						type: "DELETE_TICKET",
						payload: data.ticket.id,
					});
				}
			}

			if (data.action === "delete") {
				dispatch({
					type: "DELETE_TICKET",
					payload: data.ticketId,
				});
			}
		};

		const aoChegarMensagem = (data) => {
			if (data.action === "create" && data.ticket) {
				if (shouldUpdateTicket(data.ticket)) {
					dispatch({
						type: "UPDATE_TICKET_UNREAD_MESSAGES",
						payload: data.ticket,
					});
				}
			}
		};

		const aoAtualizarContato = (data) => {
			if (data.action === "update" && data.contact) {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		};

		const aoAtualizarEtiqueta = (data) => {
			if ((data.action === "update" || data.action === "create") && data.tag) {
				dispatch({ type: "UPDATE_TAG_NOS_TICKETS", payload: data.tag });
			}

			if (data.action === "delete" && data.tagId) {
				dispatch({ type: "REMOVE_TAG_DOS_TICKETS", payload: data.tagId });
			}
		};

		socket.on("ticket", aoAtualizarTicket);
		socket.on("appMessage", aoChegarMensagem);
		socket.on("contact", aoAtualizarContato);
		socket.on("tag", aoAtualizarEtiqueta);

		// Sempre com a função: o socket é compartilhado, e remover por nome de
		// evento derrubaria os ouvintes das outras telas.
		return () => {
			socket.off("connect", onConnect);
			socket.off("ticket", aoAtualizarTicket);
			socket.off("appMessage", aoChegarMensagem);
			socket.off("contact", aoAtualizarContato);
			socket.off("tag", aoAtualizarEtiqueta);
		};
	}, [status, searchParam, showAll, user, selectedQueueIds, ressincronizar]);

	// O numero da aba e o TOTAL, nao o tanto que ja foi carregado.
	//
	// Antes ia daqui o ticketsList.length, que e so o que o scroll ja trouxe:
	// a aba abria em "32" e virava "54" sozinha quando a pessoa rolava a lista.
	// O backend ja devolve o total pelo findAndCountAll; era so usa-lo.
	//
	// O max com o tamanho da lista cobre o intervalo entre uma busca e outra:
	// ticket que chega pelo socket entra na lista antes de existir uma contagem
	// nova do servidor, e a aba nao pode mostrar menos do que esta na tela.
	useEffect(() => {
    if (typeof updateCount === "function") {
      // Com tudo carregado, a lista na tela e a verdade: quando o socket
      // tira um ticket daqui, o numero da aba tem que cair junto. So
      // enquanto houver pagina por carregar e que vale o total do servidor.
      updateCount(
        hasMore
          ? Math.max(Number(count) || 0, ticketsList.length)
          : ticketsList.length
      );
    }

  }, [ticketsList, count, hasMore]);

	// O backend ja devolve os fixados primeiro, mas so na proxima busca. Quando
	// o usuario fixa alguem agora, precisamos reordenar aqui para o contato subir
	// na hora, sem F5. A ordem relativa do resto e preservada.
	const listaOrdenada = React.useMemo(() => {
		const peso = t => (ehFixado(t.contactId ?? t.contact?.id) ? 0 : 1);
		return [...ticketsList].sort((a, b) => peso(a) - peso(b));
	}, [ticketsList, ehFixado]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{listaOrdenada.map(ticket => (
								<TicketListItem ticket={ticket} key={ticket.id} />
							))}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
